// =============================================================================
// FEATURED REVIEWS — Horizon `Component` port of the review-card reveal behaviour (Wave 1).
// Reparents the EXACT behaviour of the bundled src/js/objects/global/featured-reviews-reveal.js
// onto Horizon's `Component` base class (`<featured-reviews>` = the section's `.featured-reviews`
// root — one element owns the whole card collection), loaded as native ESM via the `@theme/*`
// importmap (snippets/horizon-importmap.liquid) rather than the webpack src/js bundle.
//
// The reveal (scrim + stars + review + author + date overlay, styled in
// src/scss/objects/global/_featured-reviews.scss → .review-card__overlay):
//   • mouse    → :hover                   (pure CSS, `@media (hover: hover)` only)
//   • keyboard → :focus-visible           (pure CSS — this component makes each card focusable
//                                          via tabindex; NO keydown handling by design — Enter/
//                                          Space on a div does not synthesize click, and the
//                                          original never added it)
//   • touch    → .review-card--revealed   (this component toggles it on tap)
//
// PARITY NOTES (behaviour identical to the old bundled module — do not "improve"):
//   • isTouch() is evaluated LIVE PER EVENT (never cached at connect) so a convertible that
//     attaches/detaches a mouse keeps working; complements the CSS's `@media (hover: hover)`
//     scoping so iOS sticky-hover never fights the tap toggle.
//   • Tap is a TRUE TOGGLE: `willReveal` is computed BEFORE collapsing, so tapping the open card
//     closes it; tapping a closed card opens it + collapses any open sibling in the SAME
//     synchronous task (both 300ms opacity transitions run concurrently = cross-fade, and the
//     tapped card never sees a transient remove+add).
//   • Single-open is DOCUMENT-wide (collapseAll queries the whole document across all
//     `.featured-reviews` instances), exactly like the old module. Multiple instances each bind
//     their own document listener — harmless, collapseAll is idempotent.
//   • Tap outside any card closes the open one; taps inside a card early-return here (the card's
//     own listener owns them). No Escape key, no focusout, no aria-expanded, no reduced-motion
//     gate — the original has none (the reveal is input-driven, a sighted-only affordance; the
//     review copy stays in the a11y tree at all times).
//   • tabindex stays JS-set (blocks/review-card.liquid renders none) so the block's server
//     markup is unchanged; re-applied from updatedCallback after a Section Rendering morph.
//
// CHANGES vs the old module (wiring only):
//   • State class RENAMED `is-revealed` → `review-card--revealed` (BEM modifier, house
//     convention) in LOCKSTEP with the only styling consumer, _featured-reviews.scss — removes
//     the latent shared-name hazard with footer-mascot's unrelated `.ub-footer__mascot.is-revealed`.
//   • The document click listener is now per-instance, bound in connectedCallback and removed in
//     disconnectedCallback (AbortController) — fixes the old module's acknowledged never-removed
//     document-listener leak with no behavioural delta (it only ever collapses open cards).
//   • Cards are found via `ref="cards[]"` (blocks/review-card.liquid) instead of a
//     querySelectorAll; the old `data-fr-reveal` idempotency attribute (zero consumers) is
//     replaced by a WeakSet, so this component adds no data-* attributes to the DOM.
//
// `type="module"` scripts DEFER, matching the old bundle's `defer` timing (init after parse).
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it exists
// before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';

const SECTION = '.featured-reviews';
const CARD = '.review-card';
const REVEALED = 'review-card--revealed'; // lockstep with _featured-reviews.scss (was `is-revealed`)

// Touch / no-hover input — gate the tap toggle so a hover-capable mouse stays :hover-only.
// Evaluated live per event, never cached (convertible / mouse-attach correctness).
function isTouch() {
  return window.matchMedia('(hover: none)').matches;
}

// Document-wide single-open, across every `.featured-reviews` instance (parity with the old module).
function collapseAll(except) {
  document
    .querySelectorAll(`${SECTION} ${CARD}.${REVEALED}`)
    .forEach((card) => {
      if (card !== except) card.classList.remove(REVEALED);
    });
}

// Per-card bind idempotency across re-connects + editor morphs (replaces the old data-fr-reveal
// flag — zero consumers existed). Per-card listeners are never removed, exactly like the old
// module: card elements are GC'd with their DOM.
const boundCards = new WeakSet();

/**
 * The featured-reviews section root: makes each review card keyboard-focusable (tabindex → CSS
 * :focus-visible reveal) and adds the touch tap-to-reveal toggle + tap-outside-to-close.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [cards] - the `.review-card` block roots (`ref="cards[]"`); undefined
 *   when the section has no review-card blocks.
 *
 * @extends {Component<Refs>}
 */
export class FeaturedReviews extends Component {
  /** @type {AbortController | null} */
  #docAbort = null;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (synchronous) + wires Component events
    this.#bindCards();
    if (!this.#docAbort) {
      this.#docAbort = new AbortController();
      // Tap outside any card closes the open one (taps inside are the card listener's job).
      document.addEventListener('click', this.#onDocumentClick, { signal: this.#docAbort.signal });
    }
  }

  /**
   * Section Rendering morph (theme editor) — refs are re-synced by super; fresh card elements
   * (not in the WeakSet) need tabindex + the tap toggle re-applied.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#bindCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#docAbort?.abort(); // remove the document listener (the old module leaked it)
    this.#docAbort = null;
  }

  #bindCards() {
    for (const card of this.refs.cards ?? []) {
      if (boundCards.has(card)) continue; // idempotent across re-inits
      boundCards.add(card);

      // Focusable so keyboard users trigger the CSS :focus-visible reveal — without editing the
      // block's server markup.
      if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '0');

      // Touch tap toggles this card's overlay (single-open: collapse any sibling first).
      card.addEventListener('click', () => {
        if (!isTouch()) return; // hover devices use :hover; don't pin on mouse-click
        const willReveal = !card.classList.contains(REVEALED);
        collapseAll(card);
        card.classList.toggle(REVEALED, willReveal);
      });
    }
  }

  // Per-instance identity (class field arrow) so multiple instances never dedupe/steal each
  // other's document registration; bound/removed with this instance's lifecycle.
  #onDocumentClick = (event) => {
    if (!isTouch()) return;
    if (event.target.closest(`${SECTION} ${CARD}`)) return;
    collapseAll(null);
  };
}

if (!customElements.get('featured-reviews')) {
  customElements.define('featured-reviews', FeaturedReviews);
}
